import {Injectable} from '@angular/core';
import {AuthService, CurrentUser} from 'app/core/services/auth.service';
import {LocalStorageService} from 'app/core/services/local-storage.service';
import {RouterHelperService} from 'app/core/services/router-helper.service';
import {SessionStorageService} from 'app/core/services/session-storage.service';
import {Observable, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, startWith, take} from 'rxjs/operators';
import {EnvName, getEnvName} from '../../../environments/env-name';
import {environment} from '../../../environments/environment';
import {boolDevFlag} from 'test-helpers/dev-flags';

declare let window;

export enum TourPages {
    Reports,
    Monetization,
    Overview,
}

const engTours = {
    [TourPages.Reports]: '1611662875sZis6908',
    [TourPages.Monetization]: '1604922847wBle0574',
    [TourPages.Overview]: '1611670214eXnz4642',
};

const ruTours = {
    [TourPages.Reports]: '1612444340zXmp6722',
    [TourPages.Monetization]: '1612431405oGtv547',
    [TourPages.Overview]: '1612442693nPts5551',
};

const DisableTabUserPilot = 'disableTabUserPilot';
const DisableBrowserUserPilot = 'disableBrowserUserPilot';

@Injectable()
export class UserpilotService {
    private routeChanges$ = this.routerHelper.routeChanges$;
    private isDisabledByFlag = false;

    private get userpilot() {
        return window.userpilot;
    }

    constructor(
        private auth: AuthService,
        private routerHelper: RouterHelperService,
        private sessionStorage: SessionStorageService,
        private localStorage: LocalStorageService
    ) {}

    init(): void {
        this.checkDisablingFlags();

        if (this.shouldInjectProdScript()) {
            this.installScript();
        }

        this.routeChanges$.subscribe(e => {
            this.reload();
        });
    }

    shouldInjectProdScript() {
        return getEnvName() === EnvName.Prod && environment.userpilotTracking && !this.isDisabledByFlag;
    }

    checkDisablingFlags() {
        window.location.search
            .slice(1)
            .split('&')
            .forEach(elm => {
                if (elm === '') {
                    return;
                }
                const [name, value] = elm.split('=');
                if (name === DisableTabUserPilot) {
                    this.sessionStorage.setItem(name, value);
                }

                if (name === DisableBrowserUserPilot) {
                    this.localStorage.setItem(name, value);
                }
            });

        this.isDisabledByFlag =
            boolDevFlag(DisableTabUserPilot, false, this.sessionStorage) || boolDevFlag(DisableBrowserUserPilot, false, this.localStorage);
    }

    private installScript(): void {
        this.auth
            .getCurrentUserWithoutRoles() // we don't need to track users with system roles
            .then(user => [user, true])
            .then(([user, should_init]: [CurrentUser, boolean]) => {
                if (!should_init) {
                    return;
                }
                const scriptSrc = `https://deploy.userpilot.io/${environment.userpilotTracking}.js`;

                const scriptTag = document.createElement('script');
                scriptTag.id = 'userpilot-tracking-script';
                scriptTag.src = scriptSrc;
                scriptTag.onload = () => {
                    this.identify(user);
                };

                document.head.appendChild(scriptTag);
            });
    }

    private identify(user: CurrentUser): void {
        if (!this.userpilot) {
            console.debug('userpilot.identify');
            return;
        }

        this.userpilot.identify(user.id, {
            name: user.name,
            email: user.email,
            createdAt: user.createdAt,
            created_at: user.createdAt,
            canViewPublishersDashboard: user.permissions.canViewPublishersDashboard.toString(),
            isSdkIntegrated: user.isSdkIntegrated.toString(),
            hadAppsEver: user.hadAppsEver.toString(),
            hasPublishedApp: user.hasPublishedApp.toString(),
            isActiveAccount: user.isActiveAccount.toString(),
            isFirstPaymentCompleted: user.isFirstPaymentCompleted.toString(),
        });
    }

    private reload(): void {
        if (!this.userpilot) {
            console.debug('userpilot.reload');
            return;
        }

        this.userpilot.reload();
    }

    public triggerPageTourOncePerSession(page: TourPages) {
        const tourId = this.showRuTourVersion() ? ruTours[page] || engTours[page] : engTours[page];

        if (!tourId) {
            console.warn('[UserpilotService] Did not found tour for page', page);
            return;
        }
        this.triggerOncePerSession(tourId);
    }

    public triggerOncePerSession(tourIdOrSomething) {
        const key = `userpilot.triggered.${tourIdOrSomething}`;
        console.debug('userpilot.triggerOncePerSession', tourIdOrSomething);
        if (this.sessionStorage.getItem(key)) {
            return;
        }

        this.trigger(tourIdOrSomething);
        this.sessionStorage.setItem(key, '1');
    }

    public trigger(tourIdOrSomething) {
        this.routeChanges$.pipe(debounceTime(3 * 1000), take(1)).subscribe(() => {
            if (!this.userpilot) {
                console.debug('userpilot.trigger', tourIdOrSomething);
                return;
            }
            this.userpilot.trigger(tourIdOrSomething);
        });
    }

    showRuTourVersion() {
        const lang = String(navigator.language).substr(0, 2).toLowerCase();
        return ['be', 'uk', 'ru'].includes(lang);
    }

    getIsTourRunning(): Observable<boolean> {
        if (!this.userpilot) {
            return of(false);
        }

        const initialValue = typeof this.userpilot.isRunning === 'function' ? Number(this.userpilot.isRunning().current) : 0;

        // fire ID of running tour
        // if tour stopped - fire zero
        return new Observable<number>(subscriber => {
            this.userpilot.on('completed', e => {
                subscriber.next(0);
            });
            this.userpilot.on('dismissed', e => {
                subscriber.next(0);
            });
            this.userpilot.on('started', e => {
                subscriber.next(Number(e.id));
            });
            // after page reloading started event is not fired, but step event is fired
            this.userpilot.on('step', e => {
                subscriber.next(Number(e.id));
            });

            return () => {
                this.userpilot.off('started');
                this.userpilot.off('completed');
                this.userpilot.off('dismissed');
            };
        }).pipe(
            startWith(initialValue),
            debounceTime(100),
            map(id =>
                // allowed tours to load demo data
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                [90, 88, 86, 82, 84, 46].includes(id)
            ),
            distinctUntilChanged()
        );
    }
}
